import React from 'react'

import { isOffice } from '@/lib/helpers/officeHelpers'
import ContextInformation = Office.ContextInformation
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface DiagnosticModalProps {
  show: boolean
  onHide: () => void
}

// Type definitions for Office context objects
interface OfficeRequirements {
  _setMap?: {
    _sets?: Record<string, string | number | boolean>
  }
}

const DiagnosticModal: React.FC<DiagnosticModalProps> = ({ show, onHide }) => {
  function getRequirementSets(): string {
    if (!isOffice()) return 'Office.js not available'

    try {
      // Access the internal _setMap._sets object
      const setMap = (Office?.context.requirements as OfficeRequirements)
        ._setMap?._sets
      if (!setMap || typeof setMap !== 'object') {
        return 'No requirement sets found'
      }

      const sets: string[] = []
      for (const [key, value] of Object.entries(setMap)) {
        sets.push(`${key}: ${value}`)
      }

      return sets.length > 0 ? sets.join(', ') : 'No requirement sets found'
    } catch (error) {
      return `Error accessing requirement sets: ${error}`
    }
  }

  function getDiagnosticData() {
    const getOfficeValue = (path: keyof ContextInformation) => {
      if (!isOffice()) return 'Office.js not available'
      try {
        return (
          (Office?.context.diagnostics?.[path] as string) || 'Not available'
        )
      } catch (error) {
        return `Error: ${error}`
      }
    }

    return {
      host: getOfficeValue('host'),
      version: getOfficeValue('version'),
      platform: getOfficeValue('platform'),
      requirementSets: getRequirementSets(),
      userAgent: navigator.userAgent,
      cookieEnabled: navigator.cookieEnabled,
    }
  }

  function renderDiagnosticItem(title: string, content: string | boolean) {
    return (
      <div className="mb-4">
        <h6 className="mb-2">{title}</h6>
        <div className="rounded-sm border bg-gray-100 p-2">
          {String(content)}
        </div>
      </div>
    )
  }

  const data = getDiagnosticData()

  return (
    <Dialog open={show} onHide={onHide}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>🔧 Office Add-in Diagnostics</DialogTitle>
        </DialogHeader>

        <DialogBody className="max-h-[70vh] overflow-y-auto">
          {renderDiagnosticItem('Host', data.host)}
          {renderDiagnosticItem('Version', data.version)}
          {renderDiagnosticItem('Platform', data.platform)}
          {renderDiagnosticItem('Requirement Sets', data.requirementSets)}
          {renderDiagnosticItem('userAgent', data.userAgent)}
          {renderDiagnosticItem('cookieEnabled', data.cookieEnabled)}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DiagnosticModal
